import React from 'react';
import type { ClipDataPoint, Annotation, BackendDiaper, BackendMeta } from '../../lib/api-clip-monitor';
import type { DiaperModel } from '../../lib/types';
import type { AlarmEvent } from '../monitor/ClipTimeline';
import type { DiaperLinkStatus } from '../../hooks/useDiaperSelection';
import { getModelBgColor } from '../../lib/modelColors';

interface DiaperCounterWarning {
  message: string;
  periods: { start: number; end: number }[];
}

interface DiaperSelectorProps {
  selectedClipAddress: string | null;
  loadingData: boolean;
  availableDiaperCounters: number[];
  diaperLinkStatus: DiaperLinkStatus;
  dismissDiaperLinkError: () => void;
  selectedDiaperCounter: number | null;
  setSelectedDiaperCounter: (c: number) => void;
  isDiaperDropdownOpen: boolean;
  setIsDiaperDropdownOpen: (v: boolean) => void;
  diaperDropdownRef: React.RefObject<HTMLDivElement | null>;
  logicalDiaperGroups: Record<number, ClipDataPoint[]>;
  getDiaperModel: (code: number) => DiaperModel | null;
  annotations: Annotation[];
  alarmEventsByDiaper: Record<number, AlarmEvent[]>;
  backendDiaperByCounter: Record<number, BackendDiaper>;
  unmatchedBackendDiapers: BackendDiaper[];
  backendMeta: BackendMeta | null;
  filteredByDiaperCounter: ClipDataPoint[];
  diaperCounterWarning: DiaperCounterWarning | null;
  clipData: ClipDataPoint[];
}

function formatDuration(ms: number) {
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 24) return m > 0 ? `${h}h${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d${rh}h` : `${d}d`;
}

export const DiaperSelector: React.FC<DiaperSelectorProps> = ({
  selectedClipAddress,
  loadingData,
  availableDiaperCounters,
  diaperLinkStatus,
  dismissDiaperLinkError,
  selectedDiaperCounter,
  setSelectedDiaperCounter,
  isDiaperDropdownOpen,
  setIsDiaperDropdownOpen,
  diaperDropdownRef,
  logicalDiaperGroups,
  getDiaperModel,
  annotations,
  alarmEventsByDiaper,
  backendDiaperByCounter,
  unmatchedBackendDiapers,
  backendMeta,
  filteredByDiaperCounter,
  diaperCounterWarning,
  clipData,
}) => {
  const modelCodesInClip = React.useMemo(() => {
    return Array.from(new Set(
      availableDiaperCounters.map(c => {
        const records = logicalDiaperGroups[c];
        if (!records?.length) return 0;
        const active = records.find(r => r.parsed.diaperModel !== 0);
        return active ? active.parsed.diaperModel : records[0].parsed.diaperModel;
      })
    )).filter(c => c !== 0);
  }, [availableDiaperCounters, logicalDiaperGroups]);

  const hasMultipleModels = modelCodesInClip.length > 1;

  const codeColorMap = React.useMemo(() => {
    if (!hasMultipleModels) return {};
    const map: Record<number, string> = {};
    modelCodesInClip.forEach((code) => { map[code] = getModelBgColor(code); });
    return map;
  }, [modelCodesInClip, hasMultipleModels]);

  const buildLabel = React.useCallback((counter: number) => {
    const diaperData = logicalDiaperGroups[counter] || [];
    if (diaperData.length === 0) return null;
    const startTime = new Date(diaperData[0].timestamp);
    const endTime = new Date(diaperData[diaperData.length - 1].timestamp);
    const durationMs = endTime.getTime() - startTime.getTime();
    const dateStr = startTime.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    const startStr = startTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const endStr = endTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const durationStr = formatDuration(durationMs);
    const activeRec = diaperData.find(r => r.parsed.diaperModel !== 0);
    const modelCode = activeRec ? activeRec.parsed.diaperModel : diaperData[0].parsed.diaperModel;
    const modelInfo = getDiaperModel(modelCode);
    const isAllShorted = modelCode === 31;
    const isUnknownModel = modelCode > 0 && !modelInfo && !isAllShorted;
    const modelLabel = isAllShorted
      ? 'Shorted'
      : isUnknownModel
      ? `M${modelCode}?`
      : modelInfo ? `${modelInfo.name.replace(/^MediSens Abena /i, '').replace(/^Medisens Abena /i, '')}` : `M${modelCode}`;
    const diaperAnnotations = annotations.filter(a => a.diaperCounter === counter);
    const diaperTags = Array.from(new Set(diaperAnnotations.flatMap(a => a.tags)));
    const tagsStr = diaperTags.length > 0 ? ` [${diaperTags.join(', ')}]` : '';
    const badgeStr = diaperAnnotations.length > 0 ? `📌${diaperAnnotations.length}${tagsStr}` : '';
    const allAlarms = alarmEventsByDiaper[counter] || [];
    const realNonPool = allAlarms.filter(a => !a.hypothetical && a.reason !== 'Pooling').length;
    const realPool = allAlarms.filter(a => !a.hypothetical && a.reason === 'Pooling').length;
    const hypoPool = allAlarms.filter(a => a.hypothetical).length;
    const alarmStr = realNonPool ? `!! ${realNonPool}` : '';
    const poolStr = realPool ? `⚠ ${realPool}` : '';
    const hypoStr = hypoPool ? `~${hypoPool}` : '';
    const beMeta = backendDiaperByCounter[counter];
    let beBadgeStr = '';
    if (beMeta) {
      const parts: string[] = [];
      if (beMeta.emictions.length > 0) parts.push(`${beMeta.emictions.length}e`);
      const ba = beMeta.alerts;
      if (ba.wet_alert_time) parts.push('wet');
      if (ba.leak_alert_time) parts.push('leak');
      if (ba.pool_alert_time) parts.push('pool');
      beBadgeStr = parts.length > 0 ? `BE:${parts.join(',')}` : 'BE';
    }
    const text = `#${counter} • ${modelLabel} • ${dateStr} ${startStr}–${endStr} • ${durationStr} • ${diaperData.length} rec${alarmStr ? ` ${alarmStr}` : ''}${poolStr ? ` ${poolStr}` : ''}${hypoStr ? ` ${hypoStr}` : ''}`;
    return {
      text,
      counter: `#${counter}`,
      modelLabel,
      modelCode,
      dateRange: `${dateStr} ${startStr}–${endStr}`,
      duration: durationStr,
      recCount: `${diaperData.length}`,
      badge: badgeStr,
      alarm: alarmStr,
      pool: poolStr,
      hypo: hypoStr,
      hasAlarms: realNonPool > 0,
      hasPoolAlarms: realPool > 0,
      isNonDiaper: isAllShorted || isUnknownModel,
      beBadge: beBadgeStr,
    };
  }, [logicalDiaperGroups, getDiaperModel, annotations, alarmEventsByDiaper, backendDiaperByCounter]);

  const selectedLabel = selectedDiaperCounter !== null ? buildLabel(selectedDiaperCounter) : null;
  const selectedBg = selectedLabel ? codeColorMap[selectedLabel.modelCode] : undefined;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      {/* Loading indicator */}
      {selectedClipAddress && loadingData && availableDiaperCounters.length === 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
          <svg className="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading clip data and extracting diapers…
        </div>
      )}

      {/* Diaper link status banners */}
      {diaperLinkStatus === 'searching' && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-800 text-sm">
          <svg className="animate-spin h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Searching for linked diaper...
        </div>
      )}
      {diaperLinkStatus && typeof diaperLinkStatus === 'object' && 'error' in diaperLinkStatus && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm">
          <div className="flex items-start gap-2">
            <span className="text-amber-600 text-lg leading-none mt-0.5">&#9888;</span>
            <div>
              <p className="font-semibold text-amber-800">{diaperLinkStatus.error}</p>
              {diaperLinkStatus.details && (
                <p className="text-amber-700 mt-1 text-xs">{diaperLinkStatus.details}</p>
              )}
              <p className="text-amber-600 mt-1 text-xs">Select a diaper manually from the dropdown below.</p>
            </div>
            <button
              onClick={dismissDiaperLinkError}
              className="ml-auto text-amber-400 hover:text-amber-600 text-lg leading-none"
              title="Dismiss"
            >&times;</button>
          </div>
        </div>
      )}

      {/* Diaper Selection dropdown */}
      {availableDiaperCounters.length > 1 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-semibold text-gray-700">
              Select Diaper (by counter value)
            </label>
            {selectedDiaperCounter !== null && annotations.filter(a => a.diaperCounter === selectedDiaperCounter).length > 0 && (
              <span
                className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded font-medium"
                title={`${annotations.filter(a => a.diaperCounter === selectedDiaperCounter).length} annotation(s) for this diaper`}
              >
                📌 {annotations.filter(a => a.diaperCounter === selectedDiaperCounter).length}
                {' '}
                {Array.from(new Set(annotations.filter(a => a.diaperCounter === selectedDiaperCounter).flatMap(a => a.tags))).length > 0 && 
                  `[${Array.from(new Set(annotations.filter(a => a.diaperCounter === selectedDiaperCounter).flatMap(a => a.tags))).join(', ')}]`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                const idx = availableDiaperCounters.indexOf(selectedDiaperCounter!);
                if (idx < availableDiaperCounters.length - 1) {
                  setSelectedDiaperCounter(availableDiaperCounters[idx + 1]);
                }
              }}
              disabled={selectedDiaperCounter === null || availableDiaperCounters.indexOf(selectedDiaperCounter) >= availableDiaperCounters.length - 1}
              className="px-2 py-2 bg-gray-100 hover:bg-gray-200 rounded-l-lg border border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-bold"
              title="Previous diaper (older)"
            >
              ◀
            </button>

            <div ref={diaperDropdownRef} className="flex-1 relative">
              <button
                type="button"
                onClick={(e) => { setIsDiaperDropdownOpen(!isDiaperDropdownOpen); if (isDiaperDropdownOpen) (e.target as HTMLElement).blur(); }}
                className="w-full px-3 py-2 border-y border-gray-300 text-sm font-mono text-left flex items-center justify-between"
                style={selectedBg ? { backgroundColor: selectedBg } : undefined}
              >
                <span className="truncate">{selectedLabel?.text || 'Select diaper...'}</span>
                <span className="ml-2 text-gray-400">{isDiaperDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {isDiaperDropdownOpen && (
                <div className="absolute z-50 left-0 right-0 mt-0 max-h-96 overflow-y-auto border border-gray-300 rounded-b-lg shadow-lg bg-white">
                  {availableDiaperCounters.map((counter) => {
                    const info = buildLabel(counter);
                    if (!info) return null;
                    const bg = codeColorMap[info.modelCode];
                    const isSelected = counter === selectedDiaperCounter;
                    return (
                      <button
                        key={counter}
                        type="button"
                        onClick={(e) => { setSelectedDiaperCounter(counter); setIsDiaperDropdownOpen(false); (e.target as HTMLElement).blur(); }}
                        className={`w-full text-left px-1 py-0.5 text-xs font-mono hover:brightness-95 transition-colors whitespace-nowrap ${isSelected ? 'font-bold' : ''} ${info.hasAlarms ? 'border-l-2 border-red-500' : info.hasPoolAlarms ? 'border-l-2 border-yellow-500' : ''}`}
                        style={{ backgroundColor: bg || (isSelected ? '#f3f4f6' : '#ffffff') }}
                      >
                        <span className="inline-grid gap-x-2" style={{ gridTemplateColumns: '1ch 4ch 7ch 22ch 6ch 6ch 5ch 4ch 4ch auto auto' }}>
                          <span>{isSelected ? '✓' : ''}</span>
                          <span className="text-right">{info.counter}</span>
                          <span className={info.isNonDiaper ? 'text-gray-400 italic' : ''}>{info.modelLabel}</span>
                          <span>{info.dateRange}</span>
                          <span className="text-right text-gray-500">{info.duration}</span>
                          <span className="text-right">{info.recCount} rec</span>
                          <span className="text-red-600 font-bold">{info.alarm}</span>
                          <span className="text-yellow-600 font-bold">{info.pool}</span>
                          <span className="text-amber-500">{info.hypo}</span>
                          <span>{info.badge && <span className="text-purple-700">{info.badge}</span>}</span>
                          <span>{info.beBadge && <span className="text-indigo-600">{info.beBadge}</span>}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                const idx = availableDiaperCounters.indexOf(selectedDiaperCounter!);
                if (idx > 0) {
                  setSelectedDiaperCounter(availableDiaperCounters[idx - 1]);
                }
              }}
              disabled={selectedDiaperCounter === null || availableDiaperCounters.indexOf(selectedDiaperCounter) <= 0}
              className="px-2 py-2 bg-gray-100 hover:bg-gray-200 rounded-r-lg border border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-bold"
              title="Next diaper (newer)"
            >
              ▶
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            {filteredByDiaperCounter.length > 0 && (
              <>
                {new Date(filteredByDiaperCounter[0]?.timestamp).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}
                {' • '}{filteredByDiaperCounter.length} measurements
                {' • '}Diaper {availableDiaperCounters.indexOf(selectedDiaperCounter!) + 1} of {availableDiaperCounters.length}
                {backendMeta?.diapers?.length ? (
                  <>
                    {' • '}
                    <span className="text-indigo-600" title={`${Object.keys(backendDiaperByCounter).length} matched, ${unmatchedBackendDiapers.length} unmatched (extraction buffer)`}>
                      BE: {backendMeta.diapers.length} diapers ({Object.keys(backendDiaperByCounter).length} matched)
                    </span>
                    {unmatchedBackendDiapers.length > 0 && (
                      <span className="text-amber-600" title="Backend diapers from ±1 day extraction buffer that don't overlap with clip data">
                        {' '}+{unmatchedBackendDiapers.length} boundary
                      </span>
                    )}
                  </>
                ) : null}
              </>
            )}
          </p>
        </div>
      )}

      {/* Data Inconsistency Warning */}
      {diaperCounterWarning && (
        <div className="mb-4 bg-orange-50 border-2 border-orange-500 rounded-lg p-4">
          <p className="text-orange-800 font-bold text-sm mb-2">
            {diaperCounterWarning.message}
          </p>
          <p className="text-orange-700 text-xs">
            This typically means the clip has been removed and re-attached with the same counter value, which should not happen. 
            The data may be unreliable. Consider discarding this dataset.
          </p>
          <details className="mt-2">
            <summary className="text-xs text-orange-600 cursor-pointer font-semibold">
              Show time periods
            </summary>
            <ul className="text-xs text-orange-700 mt-1 ml-4 list-disc">
              {diaperCounterWarning.periods.map((period, idx) => (
                <li key={idx}>
                  Period {idx + 1}: {new Date(clipData[period.start].timestamp).toLocaleString()} 
                  {' → '}
                  {new Date(clipData[period.end].timestamp).toLocaleString()}
                  {' '}({period.end - period.start + 1} measurements)
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
};
